import React, { useEffect, useRef, useState } from 'react';

const LoopingYouTubePlayer = ({
    videoId,
    startSeconds = 0,
    endSeconds,
    isMuted = false,
    showControls = false,
    onVideoPlaying,
    className = ''
}) => {
    const playerRef = useRef(null);
    const containerRef = useRef(null);
    const [isApiReady, setIsApiReady] = useState(false);

    // Initialize the YouTube IFrame API
    useEffect(() => {
        if (!window.YT) {
            const script = document.createElement('script');
            script.src = 'https://www.youtube.com/iframe_api';
            const firstScriptTag = document.getElementsByTagName('script')[0];
            firstScriptTag.parentNode.insertBefore(script, firstScriptTag);

            window.onYouTubeIframeAPIReady = () => {
                setIsApiReady(true);
            };
        } else if (window.YT && window.YT.Player) {
            setIsApiReady(true);
        }
    }, []);

    // Polling fallback to detect API ready if the global callback was missed or hijacked
    useEffect(() => {
        const interval = setInterval(() => {
            if (window.YT && window.YT.Player) {
                setIsApiReady(true);
                clearInterval(interval);
            }
        }, 100);
        return () => clearInterval(interval);
    }, []);

    // Instantiate and manage the YT.Player instance
    useEffect(() => {
        if (!isApiReady || !videoId || !containerRef.current) return;

        let loopCheckInterval;

        // Clean up previous instance before creating a new one when props change
        if (playerRef.current && typeof playerRef.current.destroy === 'function') {
            playerRef.current.destroy();
        }

        playerRef.current = new window.YT.Player(containerRef.current, {
            videoId,
            width: '100%',
            height: '100%',
            playerVars: {
                autoplay: 1,
                mute: isMuted ? 1 : 0,
                controls: showControls ? 1 : 0,
                start: startSeconds ? Math.floor(startSeconds) : 0,
                loop: 1,
                playlist: videoId,
                modestbranding: 1,
                playsinline: 1,
                rel: 0,
                iv_load_policy: 3,
                disablekb: 1,
                fs: 0,
            },
            events: {
                onReady: (event) => {
                    if (isMuted) {
                        event.target.mute();
                    }
                    event.target.playVideo();

                    if (onVideoPlaying) {
                        setTimeout(() => onVideoPlaying(), 500);
                    }

                    let cachedDuration = 0;
                    loopCheckInterval = setInterval(() => {
                        try {
                            if (!cachedDuration || cachedDuration === 0) {
                                cachedDuration = event.target.getDuration();
                            }
                            const endLimit = endSeconds ? endSeconds : cachedDuration;
                            
                            if (endLimit > 0) {
                                const currentTime = event.target.getCurrentTime();
                                if (currentTime >= (endLimit - 0.4)) {
                                    event.target.seekTo(startSeconds || 0, true);
                                    event.target.playVideo();
                                }
                            }
                        } catch (error) {
                            // Player might not be ready or could be destroyed
                        }
                    }, 50);
                },
                onStateChange: (event) => {
                    // When video ends (state 0 / YT.PlayerState.ENDED), restart and play immediately
                    if (event.data === (window.YT?.PlayerState?.ENDED ?? 0)) {
                        event.target.seekTo(startSeconds || 0, true);
                        event.target.playVideo();
                    }
                }
            }
        });

        return () => {
            if (loopCheckInterval) {
                clearInterval(loopCheckInterval);
            }
            if (playerRef.current && typeof playerRef.current.destroy === 'function') {
                playerRef.current.destroy();
                playerRef.current = null;
            }
        };
    }, [isApiReady, videoId, startSeconds, endSeconds, isMuted, showControls, onVideoPlaying]);

    return (
        <div className={`${className} overflow-hidden relative`}>
            {/* Click-Shield */}
            <div className="absolute inset-0 z-10"></div>
            {/* The YT API will replace this inner div with an iframe */}
            <div ref={containerRef}></div>
        </div>
    );
};

export default LoopingYouTubePlayer;
